import { useMemo, useRef, useSyncExternalStore } from 'react';
import type { RepositoryControllerState } from './RepositoryController';
import { RepositoryController } from './RepositoryController';

/**
 * Хук для подписки на состояние RepositoryController.
 * Возвращает актуальный снимок состояния: { data, isLoading, error }.
 *
 * Важно: контроллер находится вне React, поэтому подписка выполнена через
 * useSyncExternalStore для корректной работы с concurrent rendering.
 */
export function useRepositoryController<T extends Record<string, unknown>>(
  controller: RepositoryController<T>
): RepositoryControllerState<T> {
  return useSyncExternalStore(
    controller.subscribe.bind(controller),
    controller.getSnapshot.bind(controller),
    controller.getSnapshot.bind(controller)
  );
}

/**
 * Селектор состояния RepositoryController.
 * Позволяет изолировать подписку на конкретный срез состояния (например, только data).
 * Использует глубокое сравнение через JSON.stringify для объектов/массивов.
 *
 * Пример:
 *   const data = useRepositorySelector(ctrl, s => s.data);
 */
export function useRepositorySelector<T extends Record<string, unknown>, S>(
  controller: RepositoryController<T>,
  selector: (state: RepositoryControllerState<T>) => S
): S {
  const state = useRepositoryController(controller);
  const selectedRef = useRef<S | undefined>(undefined);

  return useMemo(() => {
    const selected = selector(state);
    // Глубокое сравнение для объектов/массивов
    if (selectedRef.current !== undefined) {
      try {
        if (JSON.stringify(selectedRef.current) === JSON.stringify(selected)) {
          return selectedRef.current;
        }
      } catch {
        // Fallback на обычное сравнение если JSON.stringify не работает
      }
    }
    selectedRef.current = selected;
    return selected;
  }, [state, selector]);
}
